// Growth › Platforms: /api/v1/admin/platform/growth/platforms (API_CONTRACT.md ›
// Growth; backend/src/routes/admin/platform-growth-platforms.ts). The research behind
// each row comes from an idempotent seed; the owner can change three fields on one.

import { apiFetch } from './client'
import type { Bucket, ISODateTime, PlatformKind, PriceVsScoreLm, PublicPages, VsScoreLm } from './types'

const BASE = '/admin/platform/growth/platforms'

export interface PlatformSource {
  url: string
  quote: string | null
  date_checked: string | null
}

export interface Platform {
  id: number
  name: string
  slug: string
  kind: PlatformKind
  website: string | null
  host_patterns: string[]
  footer_fingerprints: string[]
  public_league_pages: PublicPages
  public_page_pattern: string | null
  public_page_example: string | null
  sports: string[]
  target_customers: string | null
  price_summary: string | null
  price_monthly_low_usd: number | null
  price_model: string | null
  /** Whatever the research wrote. Rendered as text. */
  feature_notes: unknown
  vs_scorelm: VsScoreLm
  vs_reason: string | null
  price_vs_scorelm: PriceVsScoreLm
  bucket: Bucket
  is_active: boolean
  is_target: boolean
  sources: PlatformSource[]
  /** The fields the owner set by hand: a seed leaves these alone. */
  edited_by_owner: string[]
  /** There's a source behind the row. */
  verified: boolean
  lead_count: number
  created_at: ISODateTime
  updated_at: ISODateTime
}

export interface PlatformListParams {
  kind?: PlatformKind
  bucket?: Bucket
  target?: boolean
  public_pages?: PublicPages
}

export interface PlatformListResponse {
  platforms: Platform[]
  total: number
  /** Over the other filters, ignoring the bucket filter. */
  bucket_counts: Record<Bucket | 'all', number>
}

export interface PlatformWrite {
  bucket?: Bucket
  is_target?: boolean
  vs_scorelm?: VsScoreLm
}

export interface PlatformSeedReport {
  total: number
  inserted: number
  updated: number
  unchanged: number
  /** Rows where the file disagrees with something the owner set, and the owner's value stood. */
  preserved: Array<{ slug: string; fields: string[] }>
  /** Placeholders that left the file: no sources and no owner's edit. */
  removed: string[]
  warnings: string[]
  leads_linked: number
  candidates_linked: number
  leads_moved: number
}

export function listPlatforms(params: PlatformListParams = {}, signal?: AbortSignal): Promise<PlatformListResponse> {
  return apiFetch(BASE, { query: { ...params }, signal })
}

export async function getPlatform(id: number, signal?: AbortSignal): Promise<Platform> {
  const r = await apiFetch<{ platform: Platform }>(`${BASE}/${id}`, { signal })
  return r.platform
}

/** Moves every lead on the platform, except one whose bucket was set by hand. */
export function updatePlatform(id: number, patch: PlatformWrite): Promise<{ platform: Platform; leads_moved: number }> {
  return apiFetch(`${BASE}/${id}`, { method: 'PATCH', body: patch })
}

/** Idempotent. 422 seed_invalid with every bad value in `details.problems`, and nothing changed. */
export function seedPlatforms(): Promise<PlatformSeedReport> {
  return apiFetch(`${BASE}/seed`, { method: 'POST' })
}
